/**
 * Recording how a student did on one topic — the form, and the record once it is saved.
 *
 * Two ways to assess, because teaching works both ways:
 *   questions      how many were asked, right, partly right and wrong — accuracy is worked
 *                  out as you type, so nobody does arithmetic
 *   understanding  a single score out of 100, for a theory explanation with no questions
 *
 * The caller's role decides whose record this becomes; the server will not take a parent's
 * word for a teacher's assessment, so nothing here needs to be trusted.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Check, PencilSimple, Trash, ChalkboardTeacher, UsersThree, Robot } from '@phosphor-icons/react';

/** A partially correct answer counts for half. Mirrors PARTIAL_CREDIT on the server. */
export const PARTIAL_CREDIT = 0.5;

export function computeAccuracy({ questions_asked, correct, partial, incorrect }) {
  const n = (v) => Number(v) || 0;
  const counted = n(correct) + n(partial) + n(incorrect);
  const total = n(questions_asked) || counted;
  if (!total) return null;
  const scored = n(correct) + PARTIAL_CREDIT * n(partial);
  return Math.round(Math.max(0, Math.min(100, (scored / total) * 100)) * 10) / 10;
}

const ROLE_META = {
  instructor: { label: 'Teacher', icon: ChalkboardTeacher, tone: 'bg-sky-100 text-sky-800' },
  parent: { label: 'Parent', icon: UsersThree, tone: 'bg-violet-100 text-violet-800' },
  system: { label: 'LMS quiz', icon: Robot, tone: 'bg-emerald-100 text-emerald-800' },
};

export function accuracyTone(value) {
  if (value === null || value === undefined) return 'text-muted-foreground';
  if (value >= 80) return 'text-emerald-700';
  if (value >= 50) return 'text-amber-700';
  return 'text-red-700';
}

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/** One saved assessment. Teacher, parent and the LMS's own marking all render through this. */
export function AssessmentRecord({ record, canEdit, onEdit, onDelete }) {
  const meta = ROLE_META[record.assessor_role] || ROLE_META.instructor;
  const Icon = meta.icon;
  const counts = record.method === 'questions';
  return (
    <div className="rounded-md border border-border p-2.5 space-y-1.5" data-testid={`assessment-${record.assessor_role}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase ${meta.tone}`}>
          <Icon className="w-3 h-3" weight="fill" />{meta.label}
        </span>
        <span className="text-[12px] font-medium">{record.assessor_name}</span>
        <span className="text-[11px] text-muted-foreground">{when(record.created_at)}</span>
        <span className={`ml-auto text-[15px] font-semibold tabular-nums ${accuracyTone(record.accuracy)}`} data-testid="assessment-accuracy">
          {record.accuracy === null || record.accuracy === undefined ? '—' : `${record.accuracy}%`}
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {counts
          ? `${record.correct ?? 0} correct · ${record.partial ?? 0} partly · ${record.incorrect ?? 0} wrong${record.questions_asked ? ` of ${record.questions_asked} asked` : ''}`
          : record.method === 'quiz'
            ? `Marked by the LMS${record.score !== undefined && record.score !== null ? ` · ${record.score}/${record.max_score}` : ''}`
            : 'Understanding score (no individual questions)'}
      </p>
      {record.remarks && <p className="text-[12px] whitespace-pre-wrap">{record.remarks}</p>}
      {canEdit && record.assessor_role !== 'system' && (
        <div className="flex gap-1.5 pt-0.5">
          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px]" onClick={() => onEdit(record)}>
            <PencilSimple className="w-3 h-3" /> Edit
          </Button>
          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px] text-red-700" onClick={() => onDelete(record)}>
            <Trash className="w-3 h-3" /> Remove
          </Button>
        </div>
      )}
    </div>
  );
}

const EMPTY = { method: 'questions', questions_asked: '', correct: '', partial: '', incorrect: '', understanding_score: '', remarks: '' };

export default function AssessmentForm({ initial, asRole = 'instructor', onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(initial || {}) }));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const accuracy = useMemo(
    () => (form.method === 'understanding'
      ? (form.understanding_score === '' ? null : Number(form.understanding_score))
      : computeAccuracy(form)),
    [form]
  );
  const meta = ROLE_META[asRole] || ROLE_META.instructor;

  const submit = (e) => {
    e.preventDefault();
    const num = (v) => (v === '' || v === null ? null : Number(v));
    onSave({
      method: form.method,
      questions_asked: num(form.questions_asked),
      correct: num(form.correct),
      partial: num(form.partial),
      incorrect: num(form.incorrect),
      understanding_score: num(form.understanding_score),
      remarks: form.remarks || '',
    });
  };

  return (
    <form onSubmit={submit} className="rounded-md border border-shell-primary/30 bg-shell-primary/5 p-2.5 space-y-2.5" data-testid="assessment-form">
      <p className="text-[11px] text-muted-foreground">
        Recording this as the <strong>{meta.label.toLowerCase()}</strong>'s assessment. It is kept separate from anyone else's.
      </p>

      <div className="flex gap-1.5">
        {[['questions', 'Asked questions'], ['understanding', 'Understanding only']].map(([value, label]) => (
          <button key={value} type="button" onClick={() => set({ method: value })}
            className={`h-7 px-2.5 rounded-md text-[12px] border ${form.method === value ? 'border-shell-primary bg-white font-medium' : 'border-border text-muted-foreground'}`}
            data-testid={`method-${value}`}>
            {label}
          </button>
        ))}
      </div>

      {form.method === 'questions' ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[['questions_asked', 'Asked'], ['correct', 'Correct'], ['partial', 'Partly'], ['incorrect', 'Wrong']].map(([key, label]) => (
              <div className="field" key={key}>
                <Label htmlFor={`a-${key}`}>{label}</Label>
                <Input id={`a-${key}`} type="number" min="0" value={form[key]}
                  onChange={(e) => set({ [key]: e.target.value })} data-testid={`field-${key}`} />
              </div>
            ))}
          </div>
          <p className="field-hint">A partly correct answer counts as half. Leave “Asked” blank to total the three.</p>
        </>
      ) : (
        <div className="field">
          <Label htmlFor="a-understanding">Understanding score (out of 100)</Label>
          <Input id="a-understanding" type="number" min="0" max="100" value={form.understanding_score}
            onChange={(e) => set({ understanding_score: e.target.value })} data-testid="field-understanding" />
          <p className="field-hint">For a theory explanation assessed as a whole, without individual questions.</p>
        </div>
      )}

      <div className="field field-wide">
        <Label htmlFor="a-remarks">Remarks (optional)</Label>
        <Textarea id="a-remarks" rows={2} value={form.remarks}
          onChange={(e) => set({ remarks: e.target.value })}
          placeholder="What went well, what to work on…" data-testid="field-remarks" />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[12px] text-muted-foreground">Accuracy</span>
        <span className={`text-lg font-semibold tabular-nums ${accuracyTone(accuracy)}`} data-testid="live-accuracy">
          {accuracy === null || Number.isNaN(accuracy) ? '—' : `${accuracy}%`}
        </span>
        <div className="ml-auto flex gap-2">
          {onCancel && <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>}
          <Button type="submit" size="sm" disabled={saving} data-testid="save-assessment">
            <Check className="w-4 h-4" /> {saving ? 'Saving…' : 'Save assessment'}
          </Button>
        </div>
      </div>
    </form>
  );
}
